/**
 * Map Factory Service
 *
 * Creates Google Map instances with configurable options.
 */
import { loadGoogle } from './googleMapsLoader';

/**
 * Default map options
 */
export const DEFAULT_MAP_OPTIONS = {
	zoom: 15,
	clickableIcons: false,
	scrollWheel: false,
	mapTypeControl: false,
	streetViewControl: false,
	cameraControl: false,
	zoomControl: true,
	fullscreenControl: false,
	gestureHandling: 'greedy',
	minZoom: '6',
	styles: [
		{
			featureType: 'transit',
			stylers: [{ visibility: 'off' }],
		},
	],
};

/**
 * Create a circle on the map (used for initial radius-based zoom)
 * @param {Object} google - Google Maps API object
 * @param {Object} map - Google Maps map instance
 * @param {number} radius - Radius in miles
 * @param {string} [color='#FF0000'] - Circle color
 * @returns {google.maps.Circle} Circle instance
 */
function createCircle(google, map, radius, color = '#FF0000') {
	const circleOptions = {
		center: map.getCenter(),
		fillColor: color,
		fillOpacity: 0,
		strokeOpacity: 0,
		map,
		radius: radius * 1609.34, // Convert miles to meters
	};

	return new google.maps.Circle(circleOptions);
}

/**
 * Create a Google Map instance
 *
 * @param {HTMLElement} element - DOM element to render map into
 * @param {Object} config - Configuration options
 * @param {Object} config.center - Center coordinates { latitude, longitude }
 * @param {number} [config.radius] - Initial radius in miles (for auto-zoom)
 * @param {Function} [config.onDragEnd] - Callback for drag end event
 * @param {Function} [config.onZoomEnd] - Callback when zoom level changes (triggers search like pan)
 * @param {Object} [config.mapOptions] - Additional Google Maps options to override defaults
 * @returns {Promise<{map: google.maps.Map, google: Object}>}
 */
export function createGoogleMap(element, config) {
	return loadGoogle().then((google) => {
		const { latitude, longitude } = config.center;
		const center = new google.maps.LatLng(latitude, longitude);

		const options = {
			...DEFAULT_MAP_OPTIONS,
			center,
			navigationControl: {
				style: google.maps.NavigationControlStyle.DEFAULT,
			},
			mapTypeId: google.maps.MapTypeId.ROADMAP,
			zoomControlOptions: {
				position: google.maps.ControlPosition.TOP_LEFT,
			},
			...config.mapOptions,
		};

		const map = new google.maps.Map(element, options);

		// Auto-zoom based on radius
		if (config.radius) {
			const circle = createCircle(google, map, config.radius);
			map.fitBounds(circle.getBounds());
			circle.setMap(null);
		}

		// Add drag end listener if provided
		if (typeof config.onDragEnd === 'function') {
			google.maps.event.addListener(map, 'dragend', config.onDragEnd);
		}

		if (typeof config.onZoomEnd === 'function') {
			google.maps.event.addListener(map, 'zoom_changed', config.onZoomEnd);
		}

		return { map, google };
	});
}
